import type { StorageObjectGroupListV2ApiData, StorageObjectGroupListV2ToExcelApiData } from '@navio/nsdk';
import { filterUnitFor, type SharedQuery, useFilterSpecValue, useFilterUnits } from 'noddi-ui-web';
import { STORAGE_CUSTODY_OPTIONS, storageEventCopy } from 'noddi-util';
import { useStorageAreaOptions } from '../StorageArea/useStorageAreasFilter';
import { STORAGE_FACILITY_FILTER_TEXT, useStorageFacilityOptions } from '../StorageFacility/useStorageFacilitiesFilter';
import { useStorageUnitOptions } from '../StorageUnit/useStorageUnitsFilter';

// The list, its export, select-all and the license plate preview all read the same input.
type StorageObjectGroupFilterInput = Omit<
  SharedQuery<
    NonNullable<StorageObjectGroupListV2ApiData['query']>,
    NonNullable<StorageObjectGroupListV2ToExcelApiData['query']>
  >,
  'pageIndex' | 'pageSize' | 'ordering' | 'search'
>;

export const storageObjectGroupFilterUnit = filterUnitFor<StorageObjectGroupFilterInput>();

type Props = {
  serviceDepartmentIds?: number[];
};

/** Where wheel sets are and what last happened to them. Areas narrow to the picked facilities, units to both. */
export const useStorageObjectGroupTableFilters = ({ serviceDepartmentIds }: Props = {}) => {
  const storageFacilitySpec = storageObjectGroupFilterUnit.multiSelect('storageFacilityIds', {
    ...STORAGE_FACILITY_FILTER_TEXT,
    source: useStorageFacilityOptions({ serviceDepartmentIds, heldBy: 'storageFacilityIds' }),
    exclude: true
  });
  const { storageFacilityIds } = useFilterSpecValue(storageFacilitySpec);
  const storageAreaSpec = storageObjectGroupFilterUnit.multiSelect('storageAreaIds', {
    title: 'Storage area',
    description: 'Show wheel sets stored in these areas.',
    defaultVisibleOptions: 3,
    source: useStorageAreaOptions({ storageFacilityIds, serviceDepartmentIds }),
    exclude: true
  });
  const { storageAreaIds } = useFilterSpecValue(storageAreaSpec);

  return useFilterUnits([
    storageFacilitySpec,
    storageAreaSpec,
    storageObjectGroupFilterUnit.multiSelect('storageUnitIds', {
      title: 'Storage unit',
      description: 'Show wheel sets stored at these units.',
      defaultVisibleOptions: 3,
      source: useStorageUnitOptions({ storageFacilityIds, storageAreaIds, serviceDepartmentIds }),
      exclude: true
    }),
    storageObjectGroupFilterUnit.multiSelect('custodies', {
      title: 'Custody',
      description: 'Filter by custody chain',
      options: STORAGE_CUSTODY_OPTIONS,
      exclude: true
    }),
    storageObjectGroupFilterUnit.boolean('isInStorage', {
      title: 'In storage',
      description: 'Specifies if the item is currently in storage'
    }),
    storageObjectGroupFilterUnit.boolean('isInTransit', {
      title: 'In transit',
      description: 'Specifies if the item is currently in transit'
    }),
    storageObjectGroupFilterUnit.date('missingMeasurementsAfterDate', { title: 'Missing measurements after date' }),
    storageObjectGroupFilterUnit.boolean('isPickedUpFromCustomer', {
      title: storageEventCopy.pickedUpFromCustomer.title,
      description: 'The latest event is picked up from the customer'
    }),
    storageObjectGroupFilterUnit.boolean('isDeliveredToCustomer', {
      title: storageEventCopy.deliveredToCustomer.title,
      description: 'The latest event is delivered to the customer'
    }),
    storageObjectGroupFilterUnit.boolean('isAtLoadingDock', {
      title: 'At loading dock',
      description: 'Specifies if the item is at the loading dock'
    })
  ]);
};
